/**
 * BotMessagesTab - Component hiển thị danh sách tin nhắn chatbot cho contact
 */
import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Clock, Send } from "lucide-react";
import chatbotApi from "../../../../api/chatbotApi";

const STATUS_LABELS = {
  pending: { label: "Chờ gửi", color: "text-yellow-600", bg: "bg-yellow-100" },
  scheduled: {
    label: "Đã lên lịch",
    color: "text-blue-600",
    bg: "bg-blue-100",
  },
  sent: { label: "Đã gửi", color: "text-green-600", bg: "bg-green-100" },
  success: { label: "Thành công", color: "text-green-600", bg: "bg-green-100" },
  received: { label: "Đã nhận", color: "text-green-600", bg: "bg-green-100" },
  seen: { label: "Đã xem", color: "text-purple-600", bg: "bg-purple-100" },
  failed: { label: "Thất bại", color: "text-red-600", bg: "bg-red-100" },
};

const BotMessagesTab = ({ contactId, isOpen, chatbotType }) => {
  const accountId = useSelector((state) => state.auth.user?.accountId);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const res = await chatbotApi.getLogsByContact(
        contactId,
        chatbotType,
        accountId,
      );
      if (res.status === 1) {
        setLogs(res.data || []);
      }
    } catch (error) {
      console.error("Error loading bot logs:", error);
    } finally {
      setLoading(false);
    }
  }, [accountId, chatbotType, contactId]);

  useEffect(() => {
    if (isOpen && contactId) {
      loadLogs();
    }
  }, [isOpen, contactId, loadLogs]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusInfo = (status) => {
    return (
      STATUS_LABELS[status] || {
        label: status,
        color: "text-gray-600",
        bg: "bg-gray-100",
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="flex flex-col px-4 py-2 bg-gray-50 text-gray-800 max-h-64 overflow-y-auto">
      {loading ? (
        <p className="text-sm text-gray-500">Đang tải...</p>
      ) : logs.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Chưa có tin nhắn bot nào</p>
      ) : (
        <div className="flex flex-col gap-2">
          {logs.map((log) => {
            const statusInfo = getStatusInfo(log.status);
            return (
              <div
                key={log.id}
                className="p-2 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    <span>
                      {log.dateScheduled
                        ? `Lịch gửi: ${formatDate(log.dateScheduled)}`
                        : `Gửi ngay`}
                    </span>
                  </div>
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full ${statusInfo.bg} ${statusInfo.color}`}
                  >
                    {statusInfo.label}
                  </span>
                </div>
                {log.contactQuestion && (
                  <div className="mb-1">
                    <span className="text-xs font-semibold text-gray-600">
                      Câu hỏi:
                    </span>
                    <p className="text-sm text-gray-700 truncate">
                      {log.contactQuestion}
                    </p>
                  </div>
                )}
                <div>
                  <span className="text-xs font-semibold text-blue-600">
                    Bot trả lời:
                  </span>
                  <p className="text-sm text-gray-800">{log.botReply || "-"}</p>
                </div>
                {log.dateSent && (
                  <div className="mt-1 flex items-center gap-1 text-xs text-green-600">
                    <Send className="w-3 h-3" />
                    <span>Gửi lúc: {formatDate(log.dateSent)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BotMessagesTab;
